import { forEachInSphere } from '../core/iter'
import { parseKeyX, parseKeyY, parseKeyZ, posKeyXYZ, type Position } from '../voxelMath'

import type { VoxelMap, VoxelData } from '../voxelTypes'
import type { PaintSettings, ScribbleSettings } from '@/uiState'
import { usesSurfaceOrientation, preferSeedFace } from '../voxels/registry'
import { computeVoxelFace } from '../helpers/orientation'
import { normalToFace } from '@/voxelLiterals'
import { floodFillSelect } from '@/helpers/selectionHelpers'
import { blendColor, clamp01, type BrushBlend } from '@/utils/color'

export type { BrushBlend }

export function computePaintStroke(
  params: {
    spine: Map<number, { normal?: { x: number; y: number; z: number } }>
    base: VoxelMap
    bounds: { x: number; y: number; z: number }
    scribble: Partial<ScribbleSettings>
    paint: Partial<PaintSettings>
    voxel: VoxelData
    seedNormal?: { x: number; y: number; z: number }
    visited?: Map<number, number>
    applyColor?: boolean
    applyType?: boolean
  }
): VoxelMap {
  const { spine, base, bounds, voxel, seedNormal, visited } = params
  // Backfill brush defaults so events can be partial
  const paint = params.paint || {}
  const scribble = params.scribble || {}
  const radius = Math.max(0, (scribble.radius ?? 1.5))
  const softness = clamp01(paint.softness ?? 0)
  const opacity = clamp01(paint.opacity ?? 1)
  const blendMode = (paint.blend || 'normal') as BrushBlend
  const applyColor = params.applyColor !== false
  const applyType = params.applyType !== false
  const out = new Map<number, VoxelData>()
  const r = radius
  const seed = seedNormal || { x: 0, y: 1, z: 0 }

  for (const key of spine.keys()) {
    const cx = parseKeyX(key)
    const cy = parseKeyY(key)
    const cz = parseKeyZ(key)
    forEachInSphere(cx, cy, cz, r, (x, y, z, d) => {
      const key = posKeyXYZ(x, y, z)
      const baseVoxel = base.get(key)
      if (!baseVoxel) return // Only affect existing voxels

      // Soft edge falloff
      let falloff = 1
      const rr = Math.max(1, r)
      if (softness > 0) {
        const ramp = softness * rr
        const inner = rr - ramp
        if (d <= inner) falloff = 1
        else if (d >= rr) falloff = 0
        else falloff = (rr - d) / ramp
      }
      const eff = clamp01(opacity * falloff)

      // Apply increasing opacity only within a stroke
      if (visited) {
        const prev = visited.get(key) || 0
        if (eff <= prev) return
        visited.set(key, eff)
      }

      const painted: VoxelData = { ...baseVoxel }

      // If voxel type differs and enabled, change type and compute face when oriented
      if (applyType && baseVoxel.type !== voxel.type) {
        painted.type = voxel.type
        if (usesSurfaceOrientation(voxel.type)) {
          const face = preferSeedFace(voxel.type)
            ? normalToFace(seed)
            : computeVoxelFace(base, x, y, z, bounds, seed)
          painted.face = face as any
          if (voxel.topFace) painted.topFace = voxel.topFace
        }
      }

      // Blend color when enabled
      if (applyColor) {
        const baseColor = baseVoxel.color ?? 0
        const newColor = blendColor(baseColor, voxel.color ?? 0, blendMode, eff)
        painted.color = newColor
      }
      out.set(key, painted)
    })
  }

  return out
}

export function computePaintBucket(
  params: {
    anchor: Position
    base: VoxelMap
    bounds: { x: number; y: number; z: number }
    paint: Partial<PaintSettings>
    voxel: VoxelData
    seedNormal?: { x: number; y: number; z: number }
    applyColor?: boolean
    applyType?: boolean
    matchSameType?: boolean
  }
): VoxelMap {
  const { anchor, base, bounds, voxel, seedNormal } = params
  const paint = params.paint || {}
  const opacity = clamp01(paint.opacity ?? 1)
  const blendMode = (paint.blend || 'normal') as BrushBlend
  const applyColor = params.applyColor !== false
  const applyType = params.applyType !== false
  const match = params.matchSameType !== false // default true

  const out = new Map<number, VoxelData>()
  // Determine contiguous region starting at anchor
  const keys = floodFillSelect(anchor, base, match)
  if (!keys || keys.size === 0) return out

  const seed = seedNormal || { x: 0, y: 1, z: 0 }
  for (const key of keys) {
    const baseVoxel = base.get(key)
    if (!baseVoxel) continue
    const x = parseKeyX(key)
    const y = parseKeyY(key)
    const z = parseKeyZ(key)
    const painted: VoxelData = { ...baseVoxel }

    if (applyType && baseVoxel.type !== voxel.type) {
      painted.type = voxel.type
      if (usesSurfaceOrientation(voxel.type)) {
        const face = preferSeedFace(voxel.type)
          ? normalToFace(seed)
          : computeVoxelFace(base, x, y, z, bounds, seed)
        painted.face = face as any
        if (voxel.topFace) painted.topFace = voxel.topFace
      }
    }

    if (applyColor) {
      const baseColor = baseVoxel.color ?? 0
      const newColor = blendColor(baseColor, voxel.color ?? 0, blendMode, opacity)
      painted.color = newColor
    }
    out.set(key, painted)
  }
  return out
}
